import React from 'react';
import {
  TableCell,
  TextField,
  Checkbox,
  MenuItem,
  Typography,
} from '@mui/material';
import { ImageOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import {
  DUPLICATE_PARTS_COLOR,
  PARTS_SELECTION_FONT_SIZE,
  TEXT_OVERFLOW_2LINES,
} from '../../../../../../_constants/styleConst';
import {
  MIN_PARTS_AMOUNT,
  PARTS_ATTRIBUTE,
} from '../../../../../../_constants/inspectionConst';
import { tableHeaders } from '../../RCodeTable';

export type RCodePartInfo = {
  isDuplicate: boolean;
  listNo: string;
  inFigureNo: string;
  partsAttribute: string;
  isSelect: boolean;
  selectedPartsCode: string;
  partsList: {
    partsCode: string;
    name: string;
  }[];
  amount: number;
  section: string;
  keyNo: string;
  isometricDrawing: string;
  ecnNo: string;
};

// パーツ情報のデータ配列
type PartRowProps = {
  index: number;
  rCode: string;
  part: RCodePartInfo;
  onChangeSelection: (
    isChecked: boolean,
    rCode: string,
    listNo: string,
    inFigureNo: string,
  ) => void;
  onChangePartsCode: (
    newPartsCode: string,
    rCode: string,
    listNo: string,
    inFigureNo: string,
  ) => void;
  onChangeAmount: (
    newAmount: string,
    rCode: string,
    listNo: string,
    inFigureNo: string,
  ) => void;
  handlePartsListImage: (isometricDrawing: string, section: string) => void;
};

/** Rコードに紐づくパーツ1つ分 */
const RCodePartRow: React.FC<PartRowProps> = ({
  index,
  rCode,
  part: {
    isDuplicate,
    listNo,
    inFigureNo,
    partsAttribute,
    isSelect,
    selectedPartsCode,
    partsList,
    amount,
    section,
    keyNo,
    isometricDrawing,
    ecnNo,
  },
  onChangeSelection,
  onChangePartsCode,
  onChangeAmount,
  handlePartsListImage,
}) => {
  /** 下限値を下回らないように制御 */
  const adjustAmount = () => {
    if (amount < MIN_PARTS_AMOUNT) {
      onChangeAmount(MIN_PARTS_AMOUNT.toString(), rCode, listNo, inFigureNo);
    }
  };

  return (
    <React.Fragment>
      {/* グレーアウトしない左側のセル */}
      <TableCell colSpan={4} />
      {/* チェックボックス */}
      <TableCell
        sx={{
          p: tableHeaders[4].p,
          width: tableHeaders[4].width,
          minWidth: tableHeaders[4].minWidth,
          textAlign: tableHeaders[4].textAlign,
          fontSize: PARTS_SELECTION_FONT_SIZE,
          bgcolor: isDuplicate ? DUPLICATE_PARTS_COLOR : '',
        }}
      >
        <Checkbox
          id={`RCode_PartsCode_checkbox_${index}`}
          sx={{ '& .MuiSvgIcon-root': { fontSize: 25 } }}
          disabled={partsAttribute === PARTS_ATTRIBUTE.MUST || isDuplicate}
          checked={partsAttribute === PARTS_ATTRIBUTE.MUST || isSelect}
          onChange={(e) => {
            onChangeSelection(e.target.checked, rCode, listNo, inFigureNo);
          }}
        />
      </TableCell>
      {/* パーツコード */}
      <TableCell
        sx={{
          p: tableHeaders[5].p,
          px: 0, // テキストボックスの余白分小さくする
          width: tableHeaders[5].width,
          minWidth: tableHeaders[5].minWidth,
          textAlign: tableHeaders[5].textAlign,
          bgcolor: isDuplicate ? DUPLICATE_PARTS_COLOR : '',
        }}
      >
        <TextField
          id={`RCode_selectedPartsCode_${index}`}
          size="small"
          variant="outlined"
          fullWidth
          disabled={isDuplicate}
          select={partsList.length >= 2}
          slotProps={{
            input: {
              readOnly: partsList.length === 1,
              style: { fontSize: PARTS_SELECTION_FONT_SIZE },
            },
          }}
          value={selectedPartsCode}
          onChange={(e) => {
            onChangePartsCode(e.target.value, rCode, listNo, inFigureNo);
          }}
        >
          {partsList.length > 1 &&
            partsList.map((part, i) => (
              <MenuItem
                key={`part_code_${i}`}
                value={part.partsCode}
                sx={{ fontSize: PARTS_SELECTION_FONT_SIZE }}
              >
                {part.partsCode}
              </MenuItem>
            ))}
        </TextField>
      </TableCell>
      {/* 部品名 */}
      <TableCell
        sx={{
          p: tableHeaders[6].p,
          width: tableHeaders[6].width,
          minWidth: tableHeaders[6].minWidth,
          textAlign: tableHeaders[6].textAlign,
          fontSize: PARTS_SELECTION_FONT_SIZE,
          bgcolor: isDuplicate ? DUPLICATE_PARTS_COLOR : '',
        }}
      >
        <Typography
          sx={[TEXT_OVERFLOW_2LINES, { fontSize: PARTS_SELECTION_FONT_SIZE }]}
        >
          {partsList.find((part) => part.partsCode === selectedPartsCode)?.name}
        </Typography>
      </TableCell>
      {/* キー番号 */}
      <TableCell
        key={'parts_key_no'}
        sx={{
          p: tableHeaders[7].p,
          width: tableHeaders[7].width,
          minWidth: tableHeaders[7].minWidth,
          textAlign: tableHeaders[7].textAlign,
          bgcolor: isDuplicate ? DUPLICATE_PARTS_COLOR : '',
        }}
      >
        <Typography sx={{ fontSize: PARTS_SELECTION_FONT_SIZE }}>
          {keyNo}
        </Typography>
      </TableCell>
      {/* 発注量 */}
      <TableCell
        key={'parts_amount'}
        sx={{
          p: tableHeaders[8].p,
          width: tableHeaders[8].width,
          minWidth: tableHeaders[8].minWidth,
          textAlign: tableHeaders[8].textAlign,
          bgcolor: isDuplicate ? DUPLICATE_PARTS_COLOR : '',
        }}
      >
        <TextField
          id={`RCode_amount_${index}`}
          type="number"
          size="small"
          variant="outlined"
          fullWidth
          disabled={isDuplicate}
          slotProps={{
            htmlInput: { min: MIN_PARTS_AMOUNT },
            input: { style: { fontSize: PARTS_SELECTION_FONT_SIZE } },
          }}
          // 重複パーツは表示上0にする。値を消した時0になるので一時的に空表示にする。
          value={isDuplicate ? 0 : amount === 0 ? '' : amount}
          onChange={(e) => {
            onChangeAmount(e.target.value, rCode, listNo, inFigureNo);
          }}
          // 空で確定できないように制御
          onBlur={adjustAmount}
        />
      </TableCell>
      {/* セクション */}
      <TableCell
        key={'parts_section'}
        sx={{
          p: tableHeaders[9].p,
          width: tableHeaders[9].width,
          minWidth: tableHeaders[9].minWidth,
          textAlign: tableHeaders[9].textAlign,
          bgcolor: isDuplicate ? DUPLICATE_PARTS_COLOR : '',
        }}
      >
        <Typography sx={{ fontSize: PARTS_SELECTION_FONT_SIZE }}>
          {section}
        </Typography>
      </TableCell>
      {/* パーツリストリンク */}
      <TableCell
        sx={{
          p: tableHeaders[10].p,
          width: tableHeaders[10].width,
          minWidth: tableHeaders[10].minWidth,
          textAlign: tableHeaders[10].textAlign,
          bgcolor: isDuplicate ? DUPLICATE_PARTS_COLOR : '',
          pointerEvents: isDuplicate ? 'none' : 'auto',
        }}
      >
        <Link
          to="#"
          onClick={() => handlePartsListImage(isometricDrawing, section)}
        >
          <ImageOutlined
            sx={{
              opacity: 0.7,
              width: '100%',
              alignItems: 'center',
              justifyContent: 'center',
              color: isDuplicate ? 'gray' : 'black',
              cursor: isDuplicate ? 'default' : 'pointer',
              display: isometricDrawing ? 'flex' : 'none',
              '&:hover': {
                transform: 'scale(1.2)',
                opacity: 1,
              },
            }}
          />
        </Link>
      </TableCell>
      {/* ECNファイルリンク */}
      <TableCell
        sx={{
          p: tableHeaders[11].p,
          width: tableHeaders[11].width,
          minWidth: tableHeaders[11].minWidth,
          textAlign: tableHeaders[11].textAlign,
          bgcolor: isDuplicate ? DUPLICATE_PARTS_COLOR : '',
        }}
      >
        <Typography sx={{ fontSize: PARTS_SELECTION_FONT_SIZE }}>
          {ecnNo}
        </Typography>
      </TableCell>
    </React.Fragment>
  );
};

export default RCodePartRow;
